import { Link, useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { MobileTabBar } from "@/components/MobileTabBar";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Find Jobs", to: "/jobs" },
  { label: "Courses", to: "/courses" },
  { label: "Blog", to: "/blog" },
  { label: "Pricing", to: "/#pricing" },
  { label: "For Partners", to: "/#partners" },
  { label: "For Institutes", to: "/#institutes" },
];

export function PublicNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-nav border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Job Spring home">
          <Brand dark />
        </Link>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="text-sm font-medium text-white/75 hover:text-white transition-colors"
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Button
              onClick={() => navigate(homeFor(user))}
              className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold"
              data-testid="nav-dashboard-btn"
            >
              Dashboard <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-white/85 hover:text-white hover:bg-white/10 rounded-full"
                data-testid="nav-login-btn"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate("/register?role=employer_owner")}
                className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold"
                data-testid="nav-start-hiring-btn"
              >
                Start Hiring
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="navy-bg text-white/70 noise-overlay">
      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-10 md:grid-cols-4 relative z-10">
        <div>
          <Brand dark />
          <p className="text-sm mt-4 leading-relaxed">
            Empowering Careers, Every Day. One platform for hiring, HRMS, payroll, learning and growth.
          </p>
        </div>
        <div>
          <p className="font-heading font-semibold text-white mb-4">Platform</p>
          <ul className="space-y-2.5 text-sm">
            <li><Link className="hover:text-white transition-colors" to="/jobs">Find Jobs</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/courses">Learning Marketplace</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/#pricing">Pricing</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/register?role=employer_owner">Start Hiring</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-heading font-semibold text-white mb-4">Ecosystem</p>
          <ul className="space-y-2.5 text-sm">
            <li><Link className="hover:text-white transition-colors" to="/register?role=franchise_owner">Become a Hiring Partner</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/register?role=institute_owner">Join as Training Institute</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/register?role=candidate">Create Candidate Profile</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-heading font-semibold text-white mb-4">Company</p>
          <ul className="space-y-2.5 text-sm">
            <li><span className="text-white/50">About — CMS managed</span></li>
            <li><span className="text-white/50">Contact — CMS managed</span></li>
            <li><span className="text-white/50">Careers — coming soon</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap gap-3 items-center justify-between text-xs text-white/50">
          <span>© {new Date().getFullYear()} Job Spring · Certon Technologies Private Limited. All rights reserved.</span>
          <span>Hire. Onboard. Manage. Pay. Learn. Grow.</span>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />
      <main className={`flex-1 ${user ? "pb-24 md:pb-0" : ""}`}>{children}</main>
      <Footer />
      {user && <MobileTabBar />}
    </div>
  );
}
